import React, { useRef } from 'react';
import stylesButtons from "../../../assets/styles/buttons.module.css";
import { FORM30_TABLES } from "../../../constants/form30Tables";
import { convertJsonToAppFormat } from "../../../constants/form30ColumnMapping";
import { toast } from "react-toastify";

interface ImportJsonProps {
    onImport?: (data: Record<string, Record<string, Record<string, number | null>>>) => void;
    onSave?: (data: Record<string, Record<string, Record<string, number | null>>>) => Promise<void>;
}

export const ImportJson: React.FC<ImportJsonProps> = ({ onImport, onSave }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const validateForm30Data = (data: any): boolean => {
        // Проверка что data это объект
        if (!data || typeof data !== 'object') {
            toast.error('Неверный формат файла: ожидается JSON объект');
            return false;
        }

        // Проверяем структуру данных для каждой таблицы
        for (const table of FORM30_TABLES) {
            const tableData = data[table.id];
            
            // Таблица может отсутствовать (необязательна)
            if (!tableData) continue;

            // Если таблица есть, проверяем что это объект
            if (typeof tableData !== 'object') {
                toast.error(`Неверный формат данных для таблицы "${table.title}"`);
                return false;
            }

            // Проверяем строки таблицы
            for (const row of table.rows) {
                const rowData = tableData[row.id];
                
                // Строка может отсутствовать
                if (!rowData) continue;

                // Если строка есть, проверяем что это объект
                if (typeof rowData !== 'object') {
                    toast.error(`Неверный формат данных для строки "${row.indicator}" в таблице "${table.title}"`);
                    return false;
                }

                // Проверяем колонки
                for (const column of table.columns) {
                    // Пропускаем текстовые колонки
                    if (column.type === 'text') continue;

                    const cellValue = rowData[column.key];
                    
                    // Значение может быть null или undefined
                    if (cellValue === null || cellValue === undefined) continue;

                    // Если значение есть, проверяем что это число
                    if (typeof cellValue !== 'number' || isNaN(cellValue)) {
                        toast.error(`Неверное значение в таблице "${table.title}", строка "${row.indicator}", колонка "${column.label}": ожидается число`);
                        return false;
                    }

                    // Проверяем что число неотрицательное
                    if (cellValue < 0) {
                        toast.error(`Отрицательное значение в таблице "${table.title}", строка "${row.indicator}", колонка "${column.label}"`);
                        return false;
                    }
                }
            }
        }

        return true;
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        
        if (!file) return;

        // Проверка расширения файла
        if (!file.name.endsWith('.json')) {
            toast.error('Пожалуйста, выберите JSON файл');
            event.target.value = '';
            return;
        }

        // Проверка размера файла (максимум 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            toast.error('Файл слишком большой. Максимальный размер: 5MB');
            event.target.value = '';
            return;
        }

        const reader = new FileReader();

        reader.onload = async (e) => {
            try {
                const content = e.target?.result as string;
                const jsonData = JSON.parse(content);

                // Конвертируем из формата JSON (с номерами колонок) в формат приложения (с ключами колонок)
                const appData = convertJsonToAppFormat(jsonData);

                // Валидация структуры данных
                if (!validateForm30Data(appData)) {
                    event.target.value = '';
                    return;
                }

                // Если валидация прошла успешно, обновляем state
                if (onImport) {
                    onImport(appData);
                }

                // Автоматически сохраняем данные на сервер
                if (onSave) {
                    await onSave(appData);
                    toast.success('Данные успешно импортированы и сохранены');
                } else {
                    toast.success('Данные успешно импортированы');
                }
            } catch (error) {
                toast.error('Ошибка при чтении файла: неверный формат JSON');
                console.error('JSON parse error:', error);
            }
            
            // Очищаем input для возможности повторной загрузки того же файла
            event.target.value = '';
        };

        reader.onerror = () => {
            toast.error('Ошибка при чтении файла');
            event.target.value = '';
        };

        reader.readAsText(file);
    };

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />
            <button
                onClick={handleButtonClick}
                className={`${stylesButtons.iconButton} ${stylesButtons.exportButton} w-100`}
                disabled={false}
            >
                Загрузить&nbsp;файл
            </button>
        </>
    );
};